import React, { useMemo } from 'react';
import { Modal, Table, Tag, Tooltip } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery } from '@apollo/client';
import {
  DataRetentionExecutionItem,
  DataRetentionPolicyItem,
  LIST_DATA_RETENTION_EXECUTIONS,
  ListDataRetentionExecutionsResponse,
} from '../graphql/dataRetentionQueries';

interface Props {
  /** 传 null 表示关闭 */
  policy: DataRetentionPolicyItem | null;
  onClose: () => void;
}

type ExecutionRow = DataRetentionExecutionItem & { stats: ParsedStats };

/** task_execution.status：1-初始化，2-运行中，3-成功，4-失败 */
const STATUS_META: Record<number, { text: string; color: string }> = {
  1: { text: '初始化', color: 'default' },
  2: { text: '运行中', color: 'processing' },
  3: { text: '成功', color: 'success' },
  4: { text: '失败', color: 'error' },
};

interface ParsedStats {
  deletedRows?: number;
  scannedRows?: number;
  batches?: number;
  stopReason?: string;
  cutoff?: string;
  triggerSource?: string;
  /** 执行器自己计时的真实耗时(ms)；定时触发时 task_execution.duration_seconds 恒为 0（见下方"耗时"列注释），以这个字段为准 */
  costMs?: number;
}

/** 执行统计以 JSON 存在 task_execution.message 里，这里解析出关键字段展示 */
const parseStats = (message: string | null): ParsedStats => {
  if (!message) return {};
  try {
    const parsed = JSON.parse(message);
    return (parsed?.statistics ?? {}) as ParsedStats;
  } catch {
    return {};
  }
};

const STOP_REASON_TEXT: Record<string, string> = {
  DRAINED: '已清空',
  BATCH_CAP: '达批次上限',
  TIME_BUDGET: '达时间预算',
  LOCK_BUSY: '锁占用跳过',
  ERROR: '异常',
};

const ExecutionHistoryModal: React.FC<Props> = ({ policy, onClose }) => {
  const { data, loading } = useQuery<ListDataRetentionExecutionsResponse>(
    LIST_DATA_RETENTION_EXECUTIONS,
    {
      variables: { id: policy?.id, limit: 30 },
      skip: !policy,
      fetchPolicy: 'network-only',
    },
  );

  const rows: ExecutionRow[] = useMemo(
    () =>
      (data?.listDataRetentionExecutions ?? []).map((item) => ({
        ...item,
        stats: parseStats(item.message),
      })),
    [data],
  );

  const columns: TableColumnsType<ExecutionRow> = [
    {
      title: '开始时间',
      dataIndex: 'startedAt',
      width: 190,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (status: number) => {
        const meta = STATUS_META[status] ?? { text: String(status), color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      },
    },
    {
      title: '删除行数',
      width: 110,
      render: (_, record) => record.stats.deletedRows?.toLocaleString() ?? '-',
    },
    {
      title: '批次',
      width: 80,
      render: (_, record) => record.stats.batches ?? '-',
    },
    {
      title: '结束原因',
      width: 120,
      render: (_, record) => {
        const reason = record.stats.stopReason;
        if (!reason) return '-';
        return <Tooltip title={reason}>{STOP_REASON_TEXT[reason] ?? reason}</Tooltip>;
      },
    },
    {
      title: '触发方式',
      width: 100,
      render: (_, record) => (record.stats.triggerSource === 'MANUAL' ? '手动' : '定时'),
    },
    {
      title: '耗时',
      width: 90,
      // 定时触发是"批量删除全部跑完后才补写 task_execution 记录"，
      // duration_seconds（= ended_at - started_at）因此恒为 0，不能用；
      // 改读执行器自己计时的 costMs（手动/定时两条路径共用同一段计时逻辑，均准确）
      render: (_, record) =>
        record.stats.costMs != null ? `${(record.stats.costMs / 1000).toFixed(1)}s` : '-',
    },
    {
      title: '执行机器',
      dataIndex: 'hostname',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
  ];

  return (
    <Modal
      title={policy ? `执行历史 - ${policy.tableName}` : '执行历史'}
      open={!!policy}
      onCancel={onClose}
      footer={null}
      width={1100}
    >
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
        仅记录有实际删除或执行失败的轮次；无超期数据的空轮次不落库，避免噪声。
      </div>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content', y: 420 }}
      />
    </Modal>
  );
};

export default ExecutionHistoryModal;
